import { IObservable, Subject } from '~/rage/utils';
import {
	IGameplayActivityTimer,
	IGameplayJobProgress,
	MainMenuTabs,
} from '~/shared';
import { IRpcService } from '../RpcService';
import { VarsService } from '../VarsService/VarsService';
import {
	IGameArea,
	IGameService,
	IRealTime,
	IServerNewsPost,
	IServerNewsPostShort,
	PromotionType,
} from './gameServiceTypes';

export class GameService implements IGameService {
	private realTime = new Subject<IRealTime>({
		time: '00:00',
		weekday: 'Понедельник',
		weekdayShort: 'пн',
		date: '1 января',
		dateShort: '1 янв.',
		month: 'Январь',
	});

	private serverNews = new Subject<IServerNewsPostShort[]>([
		{
			id: 'news-welcome-default',
			date: Date.now(),
			title: 'Добро пожаловать!',
			shortText: 'Новостей пока нет.',
		},
	]);

	private defaultMainMenuTab = new Subject<MainMenuTabs | null>(null);
	private interactVisible = new Subject<boolean>(false);

	private inviteVisible = new Subject<boolean>(false);
	private inviteExpirationTime = new Subject<number>(10000);
	private inviteText = new Subject<string>('');

	constructor(
		private readonly vars: VarsService,
		private readonly rpc: IRpcService,
	) {
		this.updateRealTime();
		setInterval(this.updateRealTime.bind(this), 1_000);

		this.vars
			.get<MainMenuTabs | null>('defaultMainMenuTab', null)
			.subscribe((value) => {
				this.defaultMainMenuTab.next(value);
			});
		this.vars.get('interactVisible', false).subscribe((visible) => {
			this.interactVisible.next(visible);
		});

		this.vars.get('inviteVisible', false).subscribe((visible) => {
			this.inviteVisible.next(visible);
		});
		this.vars.get('inviteExpirationTime', 10000).subscribe((time) => {
			this.inviteExpirationTime.next(time);
		});
		this.vars.get('inviteText', '').subscribe((text) => {
			this.inviteText.next(text);
		});
	}

	public getOnlinePlayers(): IObservable<number> {
		return this.vars.get('serverOnlinePlayersCount', 0);
	}

	public getActivityTimer(): IObservable<IGameplayActivityTimer | null> {
		return this.vars.get('hudActivityTimer', null);
	}

	public resetActivityTimer(): void {
		return this.vars.set('hudActivityTimer', null);
	}

	public getJobProgress(): IObservable<IGameplayJobProgress | null> {
		return this.vars.get('hudJobProgress', null);
	}

	public getGameTime(): IObservable<string> {
		return this.vars.get('gameTime', '00:00');
	}

	private updateRealTime(): void {
		const now = new Date();

		const timeFormatter = new Intl.DateTimeFormat('ru-RU', {
			timeZone: 'Europe/Moscow',
			hour: '2-digit',
			minute: '2-digit',
			hour12: false,
		});
		const time = timeFormatter.format(now).replace('.', ':');

		const weekdayFormatter = new Intl.DateTimeFormat('ru-RU', {
			timeZone: 'Europe/Moscow',
			weekday: 'long',
		});
		const weekdayFormatterShort = new Intl.DateTimeFormat('ru-RU', {
			timeZone: 'Europe/Moscow',
			weekday: 'short',
		});
		let weekday = weekdayFormatter.format(now);
		weekday = weekday[0].toUpperCase() + weekday.slice(1);
		const weekdayShort = weekdayFormatterShort.format(now);

		const dateFormatter = new Intl.DateTimeFormat('ru-RU', {
			timeZone: 'Europe/Moscow',
			day: 'numeric',
			month: 'long',
		});
		const dateFormatterShort = new Intl.DateTimeFormat('ru-RU', {
			timeZone: 'Europe/Moscow',
			day: '2-digit',
			month: 'short',
		});
		const dateShort = dateFormatterShort.format(now);
		const monthFormatter = new Intl.DateTimeFormat('ru-RU', {
			timeZone: 'Europe/Moscow',
			month: 'long',
		});
		const date = dateFormatter.format(now);
		const month =
			monthFormatter.format(now).charAt(0).toUpperCase() +
			monthFormatter.format(now).slice(1);

		this.realTime.next({ time, weekday, weekdayShort, date, dateShort, month });
	}

	public getRealTime(): IObservable<IRealTime> {
		return this.realTime;
	}

	public getArea(): IObservable<IGameArea> {
		return this.vars.get('hudGameArea', {
			primaryName: '',
			secondaryName: '',
		});
	}

	public getServerId(): IObservable<string> {
		return this.vars.get('serverId', 'loading');
	}

	public getServerName(): IObservable<string> {
		return this.vars.get('serverName', 'loading');
	}

	public getActivePromotion(): IObservable<PromotionType | null> {
		return this.vars.get('activePromotion', null);
	}

	public getDefaultMainMenuTab(): IObservable<MainMenuTabs | null> {
		return this.defaultMainMenuTab;
	}

	public async closeMainMenu(): Promise<void> {
		return await this.rpc.invokeClient('mainMenu:close');
	}

	public getServerNewsPosts(): IObservable<IServerNewsPostShort[]> {
		return this.serverNews;
	}

	public async getServerNewsPost(): Promise<IServerNewsPost | null> {
		return null;
	}

	public getInteractState(): IObservable<boolean> {
		return this.interactVisible;
	}

	public getIsInGreenZone(): IObservable<boolean> {
		return this.vars.get('isInGreenZone', false);
	}
}
